/* acaciasupplies — capture.js · la captura de email sin recarga
   (S2, feat/capture, 27 sep 2026). Lo carga `snippets/capture-boot`, detrás
   de cada bloque de captura (`notify-form`, `notify-object`).

   PROGRESSIVE ENHANCEMENT. Sin este guion el formulario se envía a /contact
   y la tienda pinta el acuse o el error con la plantilla: eso no cambia.
   Con él:

   · Validación propia (`novalidate`): un solo campo y la casilla. El error
     sale en línea, en mono, bajo el campo, con las frases ya aprobadas que
     el bloque trae en `data-msg-email` / `data-msg-consent`. Sin burbuja
     del navegador y sin recarga.
   · Envío con `fetch` al mismo `action` (/contact) con el mismo cuerpo
     (FormData: form_type, utf8, tags, email, consentimiento).
   · CÓMO SE SABE QUE HA IDO BIEN. La tienda contesta a un alta correcta con
     una redirección a la página con `?customer_posted=true`; `fetch` la
     sigue y `response.url` la trae. Si no hay redirección, se lee el HTML
     de la respuesta y se busca ESTE formulario (mismo id): con el acuse
     (`.form-msg--ok`) es un alta; con un `role="alert"` es un error, y se
     pinta su frase. Cualquier otra cosa —el desafío anti-bots de Shopify
     (`/challenge`), un 4xx/5xx, un HTML sin el formulario, la red caída—
     no se adivina: se envía el formulario de la forma normal
     (`HTMLFormElement.submit`) y la tienda sigue desde ahí. El error se
     registra antes con `console.warn` (regla 12: nada se descarta).
   · Éxito (motion v2, efecto 5): el bloque pasa a `data-noted` y el propio
     botón se convierte en la confirmación (`acaciasupplies.motion.respond`
     sobre las capas que montó `capture-boot`); el campo y la casilla se
     apagan y quedan `inert`, y «Use another email» aparece bajo la fila. Se
     recuerda en `localStorage`
     (`as:notify:<contexto>`) y se publican `as_notify` y `notify_signup`
     por la puerta de la analítica (abajo: solo con consentimiento).
   · Analítica (store-v9-1, S7): los ganchos `data-track` del contrato en
     todas las rutas (este guion lo carga también `snippets/meta-tags`, en
     el <head>). Ver «Analítica», abajo, y `docs/seo.md`.
   · «Use another email»: olvida el alta de ese contexto y devuelve el campo.
   · El error de campo se enciende por opacidad en su hueco
     (`[data-capture-msg]`, que `capture-boot` reservó antes del primer
     pintado): no desplaza el formulario.
   · Atribución: `utm_source`, `utm_medium` y `utm_campaign` de la URL de
     llegada, saneados (minúsculas, [a-z0-9._-], 40 caracteres), se guardan
     en `sessionStorage` (`as:utm`) para el resto de la visita. Al enviar:
     `src:<source>` y `cmp:<campaign>` se añaden a `contact[tags]`, junto a
     las que ya lleva (`supply-001`, `want:004`…), y los tres van a
     `contact[note]`. Sin UTM no se añade nada y la nota no se envía.

   Almacenamiento: cada acceso va en try/catch y cada fallo se registra con
   `console.warn`. Sin almacenamiento todo funciona salvo el recuerdo. */
(function (w, d) {
  'use strict';

  var as = w.acaciasupplies = w.acaciasupplies || {};
  if (as.capture) return;

  var DONE_KEY = 'as:notify:';
  var UTM_KEY = 'as:utm';
  var SUPPLY = '001';
  var FORM = 'form.notify-form';

  function warn(what, err) {
    console.warn('capture: ' + what, err);
  }

  function storage(kind) {
    try {
      return w[kind];
    } catch (err) {
      warn(kind + ' is not available', err);
      return null;
    }
  }

  function remember(key) {
    var ls = storage('localStorage');
    if (!ls) return;
    try { ls.setItem(DONE_KEY + key, String(Date.now())); } catch (err) { warn('could not remember ' + key, err); }
  }

  function forget(key) {
    var ls = storage('localStorage');
    if (!ls) return;
    try { ls.removeItem(DONE_KEY + key); } catch (err) { warn('could not forget ' + key, err); }
  }

  /* ── Atribución ──────────────────────────────────────────────────────── */

  function clean(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^[-.]+|[-.]+$/g, '')
      .slice(0, 40)
      .replace(/[-.]+$/, '');
  }

  function utm() {
    var q = new URLSearchParams(w.location.search);
    var got = { source: clean(q.get('utm_source')), medium: clean(q.get('utm_medium')), campaign: clean(q.get('utm_campaign')) };
    var ss = storage('sessionStorage');
    if (got.source || got.medium || got.campaign) {
      if (ss) {
        try { ss.setItem(UTM_KEY, JSON.stringify(got)); } catch (err) { warn('could not keep the utm values', err); }
      }
      return got;
    }
    if (!ss) return null;
    try {
      var raw = ss.getItem(UTM_KEY);
      if (!raw) return null;
      var kept = JSON.parse(raw);
      return { source: clean(kept.source), medium: clean(kept.medium), campaign: clean(kept.campaign) };
    } catch (err) {
      warn('could not read the utm values', err);
      return null;
    }
  }

  function tags(base, u) {
    var list = [base];
    if (u && u.source) list.push('src:' + u.source);
    if (u && u.campaign) list.push('cmp:' + u.campaign);
    return list.join(', ');
  }

  function note(u) {
    if (!u) return '';
    var parts = [];
    ['source', 'medium', 'campaign'].forEach(function (k) {
      if (u[k]) parts.push('utm_' + k + '=' + u[k]);
    });
    return parts.join('; ');
  }

  function attribute(form) {
    var u = utm();
    var field = form.querySelector('input[name="contact[tags]"]');
    if (field) {
      if (!field.hasAttribute('data-base-tags')) field.setAttribute('data-base-tags', field.value);
      field.value = tags(field.getAttribute('data-base-tags'), u);
    }
    var hidden = form.querySelector('[data-capture-utm]');
    if (hidden) {
      var text = note(u);
      hidden.value = text;
      hidden.disabled = !text;
    }
  }

  /* ── Analítica (store-v9-1, S7) ──────────────────────────────────────────
     Escucha los ganchos del contrato y publica con
     `Shopify.analytics.publish`, nada más (sin cookies propias, sin
     terceros; el pixel `acacia-notify` los recoge para GA4,
     `docs/seo.md`):
       view_item       al cargar, por cada `[data-track="view-item"]` (la
                       etiqueta da igual: S6 lo pone en el <article>)
       add_to_cart     clic en `[data-track="add-to-bag"]`
       pre_order       clic en `[data-track="pre-order"]`
       begin_checkout  clic en `[data-track="begin-checkout"]`
       notify_signup   alta correcta de un aviso (`done`, arriba)
       as_notify, as_object_view   los dos de feat/capture, igual que antes
     Parámetros: `item_id` (= `data-track-id`: `object.id` o «supply-001»),
     `source` (la ruta) y, en los botones dentro de un formulario de
     producto, `variant_id` y `quantity`.

     CONSENTIMIENTO. Nada sale sin el de analítica del banner
     (`assets/consent.js`, Customer Privacy API de Shopify):
     `Shopify.customerPrivacy.analyticsProcessingAllowed()`. Si la API aún
     no ha llegado (la carga `consent.js`; este guion NO vuelve a pedirla),
     los eventos esperan en una cola: se publican si llega con permiso, se
     tiran si llega sin él, y la elección del banner
     (`visitorConsentCollected`) decide la cola en el acto. Si al cabo de
     diez segundos no hay API, la cola se tira y se dice por consola, con el
     nombre de cada evento (regla 12: nada se descarta en silencio). */

  var QUEUE = [];
  var WAIT_MS = 10000;
  var STEP_MS = 250;
  var waited = 0;
  var polling = false;

  var chosen = null;

  function consent() {
    var api = w.Shopify && w.Shopify.customerPrivacy;
    if (!api || typeof api.analyticsProcessingAllowed !== 'function') return chosen;
    try {
      return !!api.analyticsProcessingAllowed();
    } catch (err) {
      warn('analyticsProcessingAllowed failed', err);
      return null;
    }
  }

  function publish(name, payload) {
    var a = w.Shopify && w.Shopify.analytics;
    if (!a || typeof a.publish !== 'function') {
      warn('Shopify.analytics is not available; ' + name + ' was not sent');
      return;
    }
    try { a.publish(name, payload); } catch (err) { warn('Shopify.analytics.publish failed for ' + name, err); }
  }

  function settle(allowed, why) {
    var pending = QUEUE.splice(0, QUEUE.length);
    if (allowed) pending.forEach(function (e) { publish(e[0], e[1]); });
    else if (allowed === null && pending.length) warn(why + '; not sent: ' + pending.map(function (e) { return e[0]; }).join(', '));
  }

  function poll() {
    if (polling) return;
    polling = true;
    (function tick() {
      var state = consent();
      if (state !== null) { polling = false; settle(state); return; }
      if (!QUEUE.length) { polling = false; return; }
      waited += STEP_MS;
      if (waited >= WAIT_MS) { polling = false; settle(null, 'no consent API after ' + WAIT_MS / 1000 + ' s'); return; }
      w.setTimeout(tick, STEP_MS);
    })();
  }

  function emit(name, payload) {
    var state = consent();
    if (state === true) return publish(name, payload);
    if (state === false) return;
    QUEUE.push([name, payload]);
    poll();
  }

  d.addEventListener('visitorConsentCollected', function (event) {
    var detail = event && event.detail;
    if (!detail || typeof detail.analyticsAllowed !== 'boolean') return;
    chosen = detail.analyticsAllowed;
    settle(chosen);
  });

  var CLICKS = { 'add-to-bag': 'add_to_cart', 'pre-order': 'pre_order', 'begin-checkout': 'begin_checkout' };

  function clicked(event) {
    var el = event.target && event.target.closest ? event.target.closest('[data-track]') : null;
    if (!el || el.tagName === 'FORM') return;
    var name = CLICKS[el.getAttribute('data-track')];
    if (!name) return;
    if (el.disabled || el.getAttribute('aria-disabled') === 'true') return;
    var payload = { item_id: el.getAttribute('data-track-id') || null, source: w.location.pathname };
    var form = el.form || el.closest('form');
    if (form) {
      var variant = form.querySelector('[name="id"]:checked, select[name="id"], input[name="id"]:not([type="radio"])');
      var qty = form.querySelector('[name="quantity"]');
      if (variant && variant.value) payload.variant_id = variant.value;
      if (name !== 'begin_checkout') payload.quantity = qty && Number(qty.value) > 0 ? Number(qty.value) : 1;
    }
    emit(name, payload);
  }

  function viewed() {
    d.querySelectorAll('[data-track="view-item"]').forEach(function (el) {
      emit('view_item', { item_id: el.getAttribute('data-track-id') || null, source: w.location.pathname });
    });
    /* `as_object_view` (feat/capture): lo deja `capture-boot` en
       `acaciasupplies.objectView` y sale por la misma puerta. */
    if (as.objectView) emit('as_object_view', as.objectView);
  }

  /* ── Mensajes ────────────────────────────────────────────────────────── */

  /* Las piezas del efecto 5 (`docs/motion.md`). Si motion.js no ha
     llegado, el mismo estado sin intermediario: el CSS hace lo demás. */
  function motion() {
    return as.motion || {
      respond: function (button, text) {
        var done = button && button.querySelector('.m-respond__done');
        if (!done) return;
        if (text) { done.textContent = text; button.setAttribute('data-motion-state', 'done'); }
        else button.removeAttribute('data-motion-state');
      },
      fieldMsg: function (slot, text) {
        if (text) { slot.textContent = text; slot.setAttribute('data-motion-state', 'on'); }
        else { slot.textContent = ''; slot.removeAttribute('data-motion-state'); }
      }
    };
  }

  /* El hueco lo pone `capture-boot` antes del primer pintado. */
  function msgFor(form) {
    var msg = form.querySelector('[data-capture-msg]');
    if (!msg) warn('no [data-capture-msg] slot in #' + form.id + ' (capture-boot did not run)');
    return msg;
  }

  function msgOn(msg) {
    return !!msg && msg.getAttribute('data-motion-state') === 'on';
  }

  function say(form, text, field) {
    /* El mensaje del servidor (sin JS) no se queda al lado del nuestro. */
    form.querySelectorAll('.form-msg[role="alert"]:not([data-capture-msg])').forEach(function (el) { el.remove(); });
    var msg = msgFor(form);
    if (!msg) return;
    motion().fieldMsg(msg, text);
    var email = form.querySelector('input[type="email"]');
    if (email) {
      email.setAttribute('aria-invalid', field === email ? 'true' : 'false');
      var ids = (email.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (ids.indexOf(msg.id) < 0) ids.push(msg.id);
      email.setAttribute('aria-describedby', ids.join(' '));
    }
    if (field && field.focus) field.focus();
  }

  function hush(form) {
    var msg = form.querySelector('[data-capture-msg]');
    if (msgOn(msg)) motion().fieldMsg(msg);
    var email = form.querySelector('input[type="email"]');
    if (email) email.removeAttribute('aria-invalid');
  }

  function check(form, box) {
    var email = form.querySelector('input[type="email"]');
    var consent = form.querySelector('input[name="contact[accepts_marketing]"]');
    if (email) email.value = email.value.trim();
    if (email && (!email.value || !email.checkValidity())) {
      say(form, box.getAttribute('data-msg-email'), email);
      return false;
    }
    if (consent && !consent.checked) {
      say(form, box.getAttribute('data-msg-consent'), consent);
      return false;
    }
    hush(form);
    return true;
  }

  /* ── Envío ───────────────────────────────────────────────────────────── */

  function objectId(box) {
    var found = /(?:^|[,\s])(?:object-|want:)(\d+)/.exec(box.getAttribute('data-capture') || '');
    return found ? 'AS-' + ('0000' + found[1]).slice(-4) : null;
  }

  function busy(form, on) {
    var button = form.querySelector('[type="submit"]');
    if (button) button.disabled = on;
    if (on) form.setAttribute('aria-busy', 'true');
    else form.removeAttribute('aria-busy');
  }

  function fallback(form, why, err) {
    warn(why + '; sending the form the normal way', err);
    busy(form, false);
    HTMLFormElement.prototype.submit.call(form);
  }

  /* El estado de hecho, o el de vuelta al campo. El botón se convierte en
     la confirmación; el campo y la casilla (`data-motion-dim`) se apagan y
     no se alcanzan con el teclado; «Use another email» sale bajo la fila. */
  function noted(box, on) {
    var button = box.querySelector('form [data-motion~="respond"]');
    var doneBox = box.querySelector('[data-capture-done]');
    if (on) {
      box.setAttribute('data-noted', '');
      box.setAttribute('data-motion-state', 'done');
    } else {
      box.removeAttribute('data-noted');
      box.removeAttribute('data-motion-state');
    }
    if (button) {
      motion().respond(button, on ? box.getAttribute('data-msg-ok') : '');
      button.inert = on;
    }
    box.querySelectorAll('[data-motion-dim]').forEach(function (el) { el.inert = on; });
    if (doneBox) doneBox.hidden = !on;
  }

  function done(form, box) {
    busy(form, false);
    hush(form);
    form.reset();
    noted(box, true);
    remember(box.getAttribute('data-capture'));
    var ok = box.querySelector('[data-capture-done] [role="status"]');
    if (ok) {
      ok.setAttribute('tabindex', '-1');
      ok.focus({ preventScroll: true });
    }
    emit('as_notify', { object_id: objectId(box), source: w.location.pathname, supply: SUPPLY });
    emit('notify_signup', { item_id: notifyId(form, box), source: w.location.pathname });
  }

  /* El id del aviso: `data-track-id` del gancho `data-track="notify"` del
     contrato (lo ponen S4 y S6 en el bloque, el formulario o el botón: se
     busca en los tres), y sin gancho, el contexto del bloque. */
  function notifyId(form, box) {
    var hook = form.closest('[data-track="notify"]') || box.querySelector('[data-track="notify"]') || (box.matches('[data-track="notify"]') ? box : null);
    var id = hook && hook.getAttribute('data-track-id');
    return id || box.getAttribute('data-capture') || null;
  }

  /* Lo que dice la respuesta: 'ok', { error } o null (no se sabe). */
  function verdict(response, html, form) {
    var url;
    try { url = new URL(response.url, w.location.href); } catch (err) { warn('unreadable response url', err); url = null; }
    if (url && /\/challenge\b/.test(url.pathname)) return null;
    if (url && url.searchParams.get('customer_posted') === 'true') return 'ok';
    if (!response.ok) return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var twin = form.id ? doc.getElementById(form.id) : null;
    if (!twin) return null;
    if (twin.querySelector('.form-msg--ok')) return 'ok';
    var alert = twin.querySelector('[role="alert"]');
    if (alert) return { error: alert.textContent.replace(/\s+/g, ' ').trim() };
    return null;
  }

  function send(form, box) {
    if (form.getAttribute('aria-busy') === 'true') return;
    attribute(form);
    var body = new FormData(form);
    busy(form, true);
    var action = (form.getAttribute('action') || '/contact').split('#')[0] || '/contact';
    w.fetch(action, { method: 'POST', body: body, credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (response) {
        return response.text().then(function (html) { return verdict(response, html, form); });
      })
      .then(function (result) {
        if (result === 'ok') return done(form, box);
        if (as.stamp && as.stamp.clearPending) as.stamp.clearPending(form);
        if (result && result.error) {
          busy(form, false);
          var consent = result.error === box.getAttribute('data-msg-consent');
          var field = form.querySelector(consent ? 'input[name="contact[accepts_marketing]"]' : 'input[type="email"]');
          say(form, result.error, field);
          return;
        }
        fallback(form, 'the store answered with something this script does not read');
      })
      .catch(function (err) {
        fallback(form, 'the request failed', err);
      });
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches(FORM)) return;
    if (event.defaultPrevented) return;
    var box = form.closest('[data-capture]');
    if (!box) return;
    event.preventDefault();
    if (!check(form, box)) return;
    send(form, box);
  }

  function again(button) {
    var box = button.closest('[data-capture]');
    if (!box) return;
    forget(box.getAttribute('data-capture'));
    noted(box, false);
    var email = box.querySelector(FORM + ' input[type="email"]');
    if (email) email.focus();
  }

  /* ── Arranque ────────────────────────────────────────────────────────── */

  d.querySelectorAll('[data-capture] ' + FORM).forEach(function (form) {
    form.setAttribute('novalidate', '');
  });
  utm();
  d.addEventListener('submit', onSubmit);
  d.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-capture-again]');
    if (button) again(button);
  });
  d.addEventListener('input', function (event) {
    var form = event.target.form;
    if (!form || !form.matches(FORM)) return;
    var msg = form.querySelector('[data-capture-msg]');
    if (msgOn(msg) && event.target.checkValidity() && (event.target.type !== 'checkbox' || event.target.checked)) hush(form);
  });

  /* En fase de captura: el clic se cuenta aunque el botón de la ficha pare
     la propagación al enviar con `fetch`. */
  d.addEventListener('click', clicked, true);
  viewed();

  as.capture = { utm: utm, tags: tags, clean: clean, emit: emit };
})(window, document);
